import { useDrupalApi } from './index'
import { addNotification, useDispatch } from '@ggs/store'
import { useI18n } from '@ggs/i18n'

/**
 * @typedef {import("../../definitions").GenericNode} GenericNode
 * @typedef {import("../../definitions").GenericMedia} GenericEntity
 */

/**
 * @typedef {GenericNode | GenericEntity} ReferenceEntity
 */

/**
 * @typedef {Object} ResolveResourceHook
 * @property {function} resolveResourceEntity
 * @property {function} notifyResourceNotFound
 */

/**
 * @typedef {Object} ResolveResourceProps
 * @property {string} resourceType
 * @property {string} resourceId
 */

/**
 * Resolve resource node entity hook.
 * @param {ResolveResourceProps} props
 * @return {ResolveResourceHook}
 */
const useResolveResourceHook = ({ resourceType, resourceId }) => {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const { getProduct, getResource, getCustomProduct, getCollection, getFabric } = useDrupalApi()

  /**
   * Resolve the node of the resource that is saved.
   * @return {Promise<ReferenceEntity>}
   */
  const resolveResourceEntity = async () => {
    // console.log(`Resolving resource ${resourceType} - ${resourceId}`)
    switch (resourceType) {
      case 'node--product':
      case 'product':
        return await getProduct(resourceId)
      case 'node--collection':
      case 'collection':
        return await getCollection(resourceId)
      case 'media--resource':
      case 'resource':
        return await getResource(resourceId)
      case 'media--custom_product':
      case 'custom_product':
        return await getCustomProduct(resourceId)
      case 'node--fabric':
      case 'fabric':
        return await getFabric(resourceId)
      default:
        return null
    }
  }

  /**
   * Dispatch resource not found notification.
   */
  const notifyResourceNotFound = () => {
    dispatch(
      addNotification(
        'resource_not_found',
        'error',
        t('Resource "{{resourceId}}" node cannot be loaded.', {
          resourceId: resourceId,
        })
      )
    )
  }

  return {
    resolveResourceEntity,
    notifyResourceNotFound,
  }
}

export default useResolveResourceHook
